// DocumentManager.js - Componente per gestione documenti CRM
// Supporta Google Drive (attuale) e pronto per migrazione a S3/Supabase

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Upload, FileText, Image, Download, Trash2, Eye, 
  File, Loader2, AlertCircle, X, Edit2, Calendar, User,
  AlertTriangle, FileX
} from 'lucide-react';
import { crmDocumentsApi } from '../lib/api';

// ==================== CONSTANTS ====================

const DOCUMENT_TYPE_LABELS = {
  contract: 'Contratto',
  identity_document: 'Documento Identità',
  tax_document: 'Documento Fiscale',
  other: 'Altro'
};

const DOCUMENT_TYPE_COLORS = {
  contract: 'bg-blue-100 text-blue-800',
  identity_document: 'bg-purple-100 text-purple-800',
  tax_document: 'bg-green-100 text-green-800',
  other: 'bg-gray-100 text-gray-800'
};

const STATUS_LABELS = {
  active: 'Attivo',
  archived: 'Archiviato',
  deleted: 'Eliminato'
};

// File status per documenti mancanti/orfani
const FILE_STATUS_CONFIG = {
  available: { label: null, color: null }, // Stato normale, nessun badge
  missing: { 
    label: 'File mancante', 
    color: 'bg-red-100 text-red-700 border-red-200',
    tooltip: 'Il file non è presente nello storage. Ricaricare il documento.'
  },
  legacy_metadata_only: { 
    label: 'Documento legacy', 
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    tooltip: 'Metadati presenti, ma file non collegato allo storage.'
  },
  orphaned: { 
    label: 'Orfano', 
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    tooltip: 'File orfano senza riferimento DB.'
  },
  unknown: { 
    label: 'Stato sconosciuto', 
    color: 'bg-gray-100 text-gray-500 border-gray-200',
    tooltip: 'Stato file non verificato.'
  }
};

const isDownloadDisabled = (doc) => {
  const fileStatus = doc.file_status;
  return fileStatus === 'missing' || fileStatus === 'legacy_metadata_only' || fileStatus === 'orphaned';
};

const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];
const MAX_SIZE_MB = 50;

// ==================== HELPER FUNCTIONS ====================

const getFileIcon = (mimeType) => {
  if (mimeType === 'application/pdf') return <FileText className="w-5 h-5 text-red-500" />;
  if (mimeType?.startsWith('image/')) return <Image className="w-5 h-5 text-blue-500" />;
  return <File className="w-5 h-5 text-gray-500" />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const validateFile = (file) => {
  if (!file) return 'Seleziona un file';
  
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return `Tipo file non consentito. Usa: ${ALLOWED_EXTENSIONS.join(', ')}`;
  }
  
  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    return `File troppo grande. Massimo ${MAX_SIZE_MB}MB.`;
  }
  
  return null;
};

// ==================== MAIN COMPONENT ====================

export const DocumentManager = ({ 
  customerId, 
  title = "Documenti",
  showHeader = true,
  compact = false 
}) => {
  // State
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [config, setConfig] = useState(null);
  
  // Modals
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  
  // Preview state
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // Upload form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('other');
  const [notes, setNotes] = useState('');
  const [dragActive, setDragActive] = useState(false);
  
  // Rename state
  const [renameDoc, setRenameDoc] = useState(null);
  const [newFilename, setNewFilename] = useState('');

  // ==================== DATA LOADING ====================

  const loadDocuments = useCallback(async () => {
    if (!customerId) return;
    
    try {
      setLoading(true);
      const response = await crmDocumentsApi.getByLead(customerId);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      // Non mostrare errore se è 403 (nessun documento)
      if (error.response?.status !== 403) {
        setDocuments([]);
      }
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const loadConfig = useCallback(async () => {
    try {
      const response = await crmDocumentsApi.getConfig();
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading document config:', error);
      // Se l'endpoint non esiste (404) o altro errore, 
      // imposta config come non configurato per evitare loader infinito
      setConfig({ configured: false, error: true });
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (customerId) {
      loadDocuments();
    }
  }, [customerId, loadDocuments]);

  // ==================== FILE HANDLERS ====================

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        toast.error(error);
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        toast.error(error);
        return;
      }
      setSelectedFile(file);
    }
  };

  // ==================== ACTIONS ====================

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Seleziona un file');
      return;
    }

    const error = validateFile(selectedFile);
    if (error) {
      toast.error(error);
      return;
    }

    try {
      setUploading(true);
      
      await crmDocumentsApi.upload(selectedFile, {
        customer_id: customerId,
        document_type: documentType,
        notes: notes || undefined
      });
      
      toast.success('Documento caricato con successo');
      resetUploadForm();
      loadDocuments();
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setDocumentType('other');
    setNotes('');
  };

  const handleDownload = async (doc) => {
    // Blocca download se file_status indica file mancante
    if (isDownloadDisabled(doc)) {
      const config = FILE_STATUS_CONFIG[doc.file_status] || FILE_STATUS_CONFIG.missing;
      toast.error(config.tooltip || 'File non disponibile', { duration: 5000 });
      return;
    }
    
    try {
      const response = await crmDocumentsApi.download(doc.id);
      const blob = new Blob([response.data], { type: doc.mime_type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download completato');
    } catch (error) {
      console.error('Download error:', error);
      
      // Gestione specifica per errore 410 (file orfano/mancante)
      if (error.response?.status === 410) {
        const errorData = error.response?.data?.detail;
        const fileStatus = errorData?.file_status || 'missing';
        const message = errorData?.message || 'File non presente nello storage. Ricaricare il documento.';
        
        toast.error(message, { duration: 8000 });
        
        // Aggiorna lo stato locale del documento se possibile
        setDocuments(prev => prev.map(d => 
          d.id === doc.id ? { ...d, file_status: fileStatus } : d
        ));
        return;
      }
      
      // Errore generico
      toast.error('Errore nel download del documento');
    }
  };

  const handlePreview = async (doc) => {
    try {
      const response = await crmDocumentsApi.getPreview(doc.id);
      if (response.data.preview_url) {
        setPreviewUrl(response.data.preview_url);
        setPreviewDoc(doc);
        setShowPreviewModal(true);
      } else {
        // Fallback: download diretto
        handleDownload(doc);
      }
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Preview non disponibile, avvio download...');
      handleDownload(doc);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${doc.original_filename}"?`)) {
      return;
    }
    
    try {
      await crmDocumentsApi.delete(doc.id);
      toast.success('Documento eliminato');
      loadDocuments();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleRename = async () => {
    if (!renameDoc || !newFilename.trim()) return;
    
    try {
      await crmDocumentsApi.rename(renameDoc.id, newFilename.trim());
      toast.success('Documento rinominato');
      setShowRenameModal(false);
      setRenameDoc(null);
      setNewFilename('');
      loadDocuments();
    } catch (error) {
      console.error('Rename error:', error);
      toast.error('Errore nella rinomina');
    }
  };

  const openRenameModal = (doc) => {
    setRenameDoc(doc);
    setNewFilename(doc.original_filename);
    setShowRenameModal(true);
  };

  // ==================== RENDER ====================

  // Loading stato iniziale
  if (config === null) {
    return (
      <Card className="card-abitalo">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF7819] mx-auto mb-3" />
          <p className="text-[#737373]">Caricamento configurazione documenti...</p>
        </CardContent>
      </Card>
    );
  }

  // Se il servizio non è configurato
  if (config && !config.configured) {
    return (
      <Card className="card-abitalo">
        <CardContent className="py-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
          <p className="text-[#737373]">
            {config.error 
              ? "Configurazione documenti non disponibile." 
              : "Sistema di archiviazione documenti non configurato."}
          </p>
          <p className="text-sm text-[#737373] mt-1">
            {config.error 
              ? "Il servizio documenti non è al momento attivo." 
              : "Contatta l'amministratore per configurare Google Drive."}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Compact view
  if (compact) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">{title} ({documents.length})</h3>
          <Button size="sm" onClick={() => setShowUploadModal(true)} className="btn-primary">
            <Upload className="w-4 h-4 mr-1" /> Carica
          </Button>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-[#FF7819]" />
          </div>
        ) : documents.length === 0 ? (
          <p className="text-sm text-[#737373] py-2">Nessun documento</p>
        ) : (
          <div className="space-y-2">
            {documents.slice(0, 5).map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-2 bg-[#F8F8F8] rounded">
                <div className="flex items-center gap-2 min-w-0">
                  {getFileIcon(doc.mime_type)}
                  <span className="text-sm truncate">{doc.original_filename}</span>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handlePreview(doc)} title="Anteprima">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} title="Download">
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            {documents.length > 5 && (
              <p className="text-xs text-[#737373] text-center">+{documents.length - 5} altri documenti</p>
            )}
          </div>
        )}

        {/* Modals */}
        {renderUploadModal()}
        {renderPreviewModal()}
      </div>
    );
  }

  // Full view
  return (
    <Card className="card-abitalo">
      {showHeader && (
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{title}</CardTitle>
              <CardDescription>
                {documents.length} document{documents.length !== 1 ? 'i' : 'o'} caricati
              </CardDescription>
            </div>
            <Button onClick={() => setShowUploadModal(true)} className="btn-primary">
              <Upload className="w-4 h-4 mr-2" /> Carica Documento
            </Button>
          </div>
        </CardHeader>
      )}
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF7819]" />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 mx-auto text-[#DCDCDC] mb-3" />
            <p className="text-[#737373]">Nessun documento caricato</p>
            <p className="text-sm text-[#737373]">Clicca "Carica Documento" per aggiungere file</p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map(doc => {
              const downloadDisabled = isDownloadDisabled(doc);
              const fileStatusConfig = FILE_STATUS_CONFIG[doc.file_status];
              
              return (
              <div 
                key={doc.id} 
                className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                  downloadDisabled 
                    ? 'border-red-200 bg-red-50/30 hover:bg-red-50/50' 
                    : 'border-[#DCDCDC] hover:bg-[#F8F8F8]'
                }`}
              >
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  {downloadDisabled ? (
                    <FileX className="w-5 h-5 text-red-400" />
                  ) : (
                    getFileIcon(doc.mime_type)
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-medium truncate ${downloadDisabled ? 'text-gray-400' : ''}`}>
                        {doc.original_filename}
                      </p>
                      <Badge className={DOCUMENT_TYPE_COLORS[doc.document_type] || DOCUMENT_TYPE_COLORS.other}>
                        {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
                      </Badge>
                      {/* Badge file_status per documenti mancanti/legacy */}
                      {fileStatusConfig?.label && (
                        <Badge className={fileStatusConfig.color} title={fileStatusConfig.tooltip}>
                          {doc.file_status === 'missing' && <FileX className="w-3 h-3 mr-1" />}
                          {doc.file_status === 'legacy_metadata_only' && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {fileStatusConfig.label}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-[#737373] mt-1">
                      <span>{formatFileSize(doc.size_bytes)}</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(doc.created_at)}
                      </span>
                      {doc.uploaded_by_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {doc.uploaded_by_name}
                        </span>
                      )}
                    </div>
                    {doc.notes && (
                      <p className="text-xs text-[#737373] italic mt-1">{doc.notes}</p>
                    )}
                    {/* Messaggio per file mancante */}
                    {downloadDisabled && (
                      <p className="text-xs text-red-600 mt-1">
                        {fileStatusConfig?.tooltip || 'File non disponibile'}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 ml-4">
                  <Button variant="ghost" size="sm" onClick={() => handlePreview(doc)} title="Anteprima">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => !downloadDisabled && handleDownload(doc)} 
                    disabled={downloadDisabled}
                    title={downloadDisabled ? 'File non disponibile - Ricaricare il documento' : 'Download'}
                    className={downloadDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                  >
                    {downloadDisabled ? (
                      <FileX className="w-4 h-4 text-red-400" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openRenameModal(doc)} title="Rinomina">
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleDelete(doc)} 
                    title="Elimina" 
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
            })}
          </div>
        )}
      </CardContent>

      {/* Modals */}
      {renderUploadModal()}
      {renderPreviewModal()}
      {renderRenameModal()}
    </Card>
  );

  // ==================== MODAL RENDERERS ====================

  function renderUploadModal() {
    return (
      <Dialog open={showUploadModal} onOpenChange={(open) => { if (!open) resetUploadForm(); else setShowUploadModal(true); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Carica Documento</DialogTitle>
            <DialogDescription>
              Carica un file PDF, JPG o PNG (max {MAX_SIZE_MB}MB)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Drop zone */}
            <div 
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                dragActive ? 'border-[#FF7819] bg-orange-50' : 'border-[#DCDCDC] hover:border-[#FF7819]'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById('doc-upload-input')?.click()}
            >
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  {getFileIcon(selectedFile.type)}
                  <div className="text-left">
                    <p className="font-medium">{selectedFile.name}</p>
                    <p className="text-sm text-[#737373]">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto text-[#737373] mb-3" />
                  <p className="font-medium">Trascina un file qui</p>
                  <p className="text-sm text-[#737373] mt-1">oppure clicca per selezionare</p>
                  <p className="text-xs text-[#737373] mt-2">{ALLOWED_EXTENSIONS.join(', ').toUpperCase()} - Max {MAX_SIZE_MB}MB</p>
                </>
              )}
              <input 
                id="doc-upload-input"
                type="file" 
                accept={ALLOWED_EXTENSIONS.join(',')}
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
            
            {/* Document type */}
            <div>
              <Label>Tipo Documento</Label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Notes */}
            <div>
              <Label>Note (opzionale)</Label>
              <Textarea 
                value={notes} 
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Aggiungi note sul documento..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetUploadForm}>Annulla</Button>
            <Button onClick={handleUpload} disabled={!selectedFile || uploading} className="btn-primary">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Carica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function renderPreviewModal() {
    return (
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewDoc && getFileIcon(previewDoc.mime_type)}
              {previewDoc?.original_filename}
            </DialogTitle>
          </DialogHeader>
          <div className="h-[70vh] bg-[#F8F8F8] rounded">
            {previewUrl && (
              <iframe 
                src={previewUrl} 
                className="w-full h-full border-0 rounded"
                title="Document Preview"
                sandbox="allow-same-origin allow-scripts"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>Chiudi</Button>
            <Button onClick={() => previewDoc && handleDownload(previewDoc)} className="btn-primary">
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  function renderRenameModal() {
    return (
      <Dialog open={showRenameModal} onOpenChange={setShowRenameModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rinomina Documento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nuovo nome file</Label>
              <Input 
                value={newFilename} 
                onChange={(e) => setNewFilename(e.target.value)}
                placeholder="Nome file..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenameModal(false)}>Annulla</Button>
            <Button onClick={handleRename} disabled={!newFilename.trim()} className="btn-primary">
              Rinomina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
};

export default DocumentManager;
